/**
 * Aged Care Compliance Dashboard
 * Real-time compliance monitoring and gap identification for Aged Care facilities
 * Tracks: Care Minutes, Star Ratings, AN-ACC, NQIP, SIRS, Quality Indicators
 */

import React, { useState, useEffect } from 'react';
import { 
  Shield, AlertTriangle, CheckCircle, TrendingUp, TrendingDown,
  Target, BarChart3, Clock, FileText
} from 'lucide-react';
import AutomatedComplianceService from '../../services/compliance/AutomatedComplianceService';
import { useAppContext } from '../../context/AppContext';

const ComplianceDashboard = ({ facilityId }) => {
  const { state } = useAppContext();
  const selectedFacility = facilityId || state.selectedFacility;
  
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCompliance();
  }, [selectedFacility]);

  const loadCompliance = async () => {
    setLoading(true);
    try {
      const result = await AutomatedComplianceService.getComplianceDashboard(selectedFacility);
      if (result.success) {
        setCompliance(result.data);
      }
    } catch (error) {
      console.error('Error loading compliance:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRunCheck = async () => {
    setLoading(true);
    try {
      const result = await AutomatedComplianceService.runComplianceCheck(selectedFacility);
      if (result.success) {
        setCompliance(result.data);
      }
    } catch (error) {
      console.error('Error running compliance check:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !compliance) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!compliance) {
    return (
      <div className="text-center p-8">
        <p className="text-gray-500 mb-4">No compliance data available</p>
        <button
          onClick={handleRunCheck}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Run Compliance Check
        </button>
      </div>
    );
  }

  const overallScore = compliance.overallScore || 0;
  const statusColor = overallScore >= 95 ? 'green' : overallScore >= 80 ? 'yellow' : 'red';
  const statusText = overallScore >= 95 ? 'Compliant' : overallScore >= 80 ? 'At Risk' : 'Non-Compliant';

  return (
    <div className="space-y-6">
      {/* Overall Status */}
      <div className={`bg-gradient-to-r from-${statusColor}-600 to-${statusColor}-700 rounded-2xl p-8 text-white`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">Overall Compliance Score</h2>
            <div className="flex items-center gap-4">
              <div className="text-5xl font-bold">{overallScore}%</div>
              <div>
                <div className="text-lg font-semibold">{statusText}</div>
                <div className="text-sm opacity-90">
                  Last checked: {new Date(compliance.checkDate).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={handleRunCheck}
            className="px-6 py-3 bg-white text-blue-600 rounded-lg hover:bg-blue-50 font-semibold"
          >
            Run Check
          </button>
        </div>
      </div>

      {/* Component Scores */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {Object.entries(compliance.checks).map(([key, check]) => {
          if (key === 'facilityId' || key === 'checkDate') return null;
          
          const isCompliant = check.compliant;
          const score = check.score || 0;
          
          return (
            <div
              key={key}
              className={`bg-white rounded-xl p-6 shadow-sm border ${
                isCompliant ? 'border-green-200' : 'border-red-200'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800 capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </h3>
                {isCompliant ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                )}
              </div>
              <div className="text-3xl font-bold mb-2" style={{ color: isCompliant ? '#10b981' : '#ef4444' }}>
                {score}%
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${isCompliant ? 'bg-green-600' : 'bg-red-600'}`}
                  style={{ width: `${score}%` }}
                ></div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Gaps & Recommendations */}
      {compliance.gaps && compliance.gaps.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            Compliance Gaps
          </h3>
          
          <div className="space-y-4">
            {compliance.gaps.map((gap, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border ${
                  gap.priority === 'high'
                    ? 'bg-red-50 border-red-200'
                    : gap.priority === 'medium'
                    ? 'bg-yellow-50 border-yellow-200'
                    : 'bg-blue-50 border-blue-200'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-semibold text-gray-800 capitalize">
                      {gap.area.replace(/([A-Z])/g, ' $1').trim()}
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">Score: {gap.score}%</p>
                  </div>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      gap.priority === 'high'
                        ? 'bg-red-100 text-red-800'
                        : gap.priority === 'medium'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {gap.priority} priority
                  </span>
                </div>
                {gap.issues && gap.issues.length > 0 && (
                  <ul className="list-disc list-inside text-sm text-gray-700 mt-2">
                    {gap.issues.map((issue, i) => (
                      <li key={i}>{issue}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {compliance.recommendations && compliance.recommendations.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-600" />
            Recommendations
          </h3>
          
          <div className="space-y-3">
            {compliance.recommendations.map((rec, index) => (
              <div key={index} className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-gray-800">{rec.area}</h4>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      rec.priority === 'high'
                        ? 'bg-red-100 text-red-800'
                        : rec.priority === 'medium'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                    }`}
                  >
                    {rec.priority}
                  </span>
                </div>
                <p className="text-sm text-gray-700 mb-2">{rec.action}</p>
                <div className="flex items-center gap-4 text-xs text-gray-600">
                  <span>Impact: {rec.impact}</span>
                  <span>Timeline: {rec.timeline}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceDashboard;
